import { t } from '@/config/i18n';
/* TYPES */
import type {
  PredictiveModelGraphShapeCluster,
  PredictiveModelGraphZone
} from '@/types/api/predictiveModels/predictiveModels';
import type { PMCluster, PmCode, RadarFormattedData } from '@/types/predictiveModels/predictiveModels.types';

/**
 * Get the predictive model name with translation if needed
 *
 * @param {string} pmName Predictive model name
 */
export function getPredictiveModelName(name: string, type: PmCode = 'benchmark') {
  // Translation PM name if translation key
  if (type === 'benchmark') {
    return t(name);
  }

  return name;
}

/**
 * Format a dimension id to a zone for edition and creation
 *
 * @param {number} dimensionId Id of the dimension
 * @param {boolean} expectedHigh Is the dimension is expected for high value or low value
 */
export function getPMDimensionZone(dimensionId: number, expectedHigh = true): PredictiveModelGraphZone[] {
  if (expectedHigh) {
    return [
      {
        type: 'EXPECTED',
        dimension: dimensionId,
        min: 7,
        max: null,
        coefficient: 1
      },
      {
        type: 'NEUTRAL',
        dimension: dimensionId,
        min: 5,
        max: 7,
        coefficient: 1
      },
      {
        type: 'AVOIDED',
        dimension: dimensionId,
        min: 1,
        max: 5,
        coefficient: 1
      }
    ];
  } else {
    return [
      {
        type: 'EXPECTED',
        dimension: dimensionId,
        min: 1,
        max: 5,
        coefficient: 1
      },
      {
        type: 'NEUTRAL',
        dimension: dimensionId,
        min: 5,
        max: 7,
        coefficient: 1
      },
      {
        type: 'AVOIDED',
        dimension: dimensionId,
        min: 7,
        max: null,
        coefficient: 1
      }
    ];
  }
}

// RADAR

/**
 * Get shape dimensions by clusters formatted for radar usage
 * @param categories - API data for radar
 * @param showUserScore - should the user scores for each dimension be displayed
 * @returns formatted data
 */
export function getRadarData(categories: PredictiveModelGraphShapeCluster[], showUserScore = true): RadarFormattedData {
  if (!categories) {
    return false as unknown as RadarFormattedData;
  }

  // Get expected drive dimensions
  const categoriesByCluster = categories.reduce(
    (acc, cluster, index) => {
      const category = acc[index];
      if (!category) return acc;

      // Set category label
      category.label = cluster.label;

      // Set all dimensions in the cluster
      cluster.attributes.forEach(dimension => {
        // Get selected range if high requested or low by checking the shape values
        let dimensionSelectedRange: -1 | 0 | 1 = 0; // Neutral so not selected
        // API call return an empty array if the dimension is not taken
        if (!Array.isArray(dimension.ranges)) {
          // No choice to make this since some dimension does not have expected or neutral
          /**
           * @WARNING : dimension can sometimes have only avoided
           */
          if (dimension.ranges.expected) {
            dimensionSelectedRange = dimension.ranges.expected[0].every(val => [7, 10].includes(val)) ? 1 : -1;
          } else if (dimension.ranges.avoided) {
            dimensionSelectedRange = dimension.ranges.avoided[0].every(val => [7, 10].includes(val)) ? -1 : 1;
          }
        }

        // Store the dimension in the cluster list
        category.dimensions.push({
          id: dimension.id,
          value: dimension.value,
          label: dimension.label,
          labelOpposite: dimension.label_opposite,
          selectedRange: dimensionSelectedRange
        });
      });

      return acc;
    },
    [
      { id: 1, label: '', dimensions: [] as PMCluster['dimensions'] },
      { id: 2, label: '', dimensions: [] as PMCluster['dimensions'] },
      { id: 3, label: '', dimensions: [] as PMCluster['dimensions'] },
      { id: 4, label: '', dimensions: [] as PMCluster['dimensions'] },
      { id: 5, label: '', dimensions: [] as PMCluster['dimensions'] }
    ]
  );
  return categoriesByCluster?.reduce(
    (acc, cluster) => {
      // Push cluster label
      acc.clusterLabelList.push(cluster.label);

      // Push dimensions of cluster and push is expected state
      cluster.dimensions.forEach(dimension => {
        acc.dimensionLabelList.push(dimension.label.replace(/\(\d*\)/, ''));
        acc.dimensionRangeStatusList.push(dimension.selectedRange);
        if (showUserScore && typeof dimension.value !== 'undefined') acc.dimensionShapeScoreList.push(dimension.value);
      });
      return acc;
    },
    {
      clusterLabelList: [] as string[],
      dimensionLabelList: [] as string[],
      dimensionRangeStatusList: [] as Array<-1 | 0 | 1>,
      dimensionShapeScoreList: [] as number[]
    }
  );
}
